import { toFormat, isInFuture } from "@/lib/dateHelpers";
import {
  getStatusTagColor,
  getCompletionLabel,
  filterAndSortRequirements
} from "@/features/person/utils/pgm-requirements";
import type { EducationPgmProgram, PgmProgramRequirement } from "@/types";

export type TestGroupKeySource = {
  testId: string;
  testDefinition?: { oldTestId?: string | null } | null;
};

export const getTestGroupKey = (test: TestGroupKeySource): string => {
  if (test.testDefinition?.oldTestId) return test.testDefinition.oldTestId;

  return test.testId.replace(/TEST(?:1|R)$/, "TEST");
};

export const deduplicateTests = (
  tests: NonNullable<EducationPgmProgram["testHistory"]>
): NonNullable<EducationPgmProgram["testHistory"]> => {
  const bestByTestGroup = new Map<string, (typeof tests)[number]>();
  for (const t of tests) {
    const groupKey = getTestGroupKey(t);
    const existing = bestByTestGroup.get(groupKey);
    if (
      !existing ||
      (t.isPassed && !existing.isPassed) ||
      (!existing.isPassed && !t.isPassed && t.attemptNumber > existing.attemptNumber)
    ) {
      bestByTestGroup.set(groupKey, t);
    }
  }
  return Array.from(bestByTestGroup.values());
};

export type ProgramDetailType = "Requirement" | "Test";

export interface ProgramDetailRow {
  id: string;
  type: ProgramDetailType;
  requireTypeLabel?: string;
  description: string;
  secondaryText: string;
  requireId?: string;
  levelCode: string;
  date: string | null;
  completed: boolean;
  statusLabel: string;
  statusType: "success" | "warn" | "error" | "surface" | "information";
  scoreText?: string | null;
  requirement?: PgmProgramRequirement;
  levelStartDate?: string | null;
  archived?: boolean;
}

export interface ProgramProgressRow {
  id: string;
  archived: boolean;
  level: string;
  code: string;
  version: string;
  stage: string;
  startDate: string | null;
  endDate: string | null;
  startDateIso: string;
  endDateIso: string | null;
  completeCode: string | null;
  remarks: string | null;
  detailsCount: number;
  completedDetailsCount: number;
  status: string;
  completed: boolean;
  expiresDate?: string | null;
  isExpired?: boolean;
  details: ProgramDetailRow[];
}

const isQualifyingLevel = (level: number) => level === 0;

export const formatProgramData = (programHistory: EducationPgmProgram[]): ProgramProgressRow[] => {
  return programHistory.map((level) => {
    const isArchived = !!level.archived;
    const id = `${isArchived ? "archived" : "live"}-${level.levelCode}-${level.startDate ?? ""}`;
    const sortedReqs = level.programRequirements
      ? filterAndSortRequirements(level.programRequirements, level.levelCode)
      : [];

    const requirementRows: ProgramDetailRow[] = sortedReqs.map((req) => {
      const statusLabel = getCompletionLabel(req.isCompleted, req.completionStatus, { archived: isArchived });
      const statusType = getStatusTagColor(statusLabel);
      return {
        id: `${id}-req-${req.requireId}`,
        type: "Requirement" as ProgramDetailType,
        description: req.requirementDefinition?.description || "Unknown requirement",
        secondaryText: req.requireId,
        requireId: req.requireId,
        levelCode: req.levelCode,
        date: req.completionDate ? toFormat(req.completionDate) : null,
        completed: req.isCompleted,
        statusLabel,
        statusType,
        scoreText: req.score != null ? `${req.score}` : null,
        requirement: req,
        levelStartDate: level.startDate,
        archived: isArchived
      };
    });

    const levelTests = isArchived ? [] : (level.testHistory || []).filter((t) => t.levelCode === level.levelCode);
    const dedupedTests = deduplicateTests(levelTests);
    const testRows: ProgramDetailRow[] = dedupedTests.map((test) => ({
      id: `${id}-test-${test.testId}-${test.attemptNumber}`,
      type: "Test" as ProgramDetailType,
      description: test.testDefinition?.description || test.testId,
      secondaryText: test.testId,
      levelCode: test.levelCode,
      date: test.testDate ? toFormat(test.testDate) : null,
      completed: test.isPassed,
      statusLabel: test.isPassed ? "Passed" : test.isFailed ? "Failed" : "Not Attempted",
      statusType: test.isPassed ? "success" : test.isFailed ? "error" : "surface",
      scoreText: test.score != null ? `${test.score}/${test.maxScore}` : null,
      archived: isArchived
    }));

    const details = [...requirementRows, ...testRows];

    return {
      id,
      archived: isArchived,
      level: isQualifyingLevel(level.level)
        ? "Qualifying Level"
        : level.careerPath
          ? `Level ${level.level} ${level.careerPath}`
          : `Level ${level.level}`,
      code: level.levelCode,
      version: level.curriculumVersion,
      stage: isArchived ? "Archived" : level.completed ? "Completed" : "Started",
      startDate: level.startDate ? toFormat(level.startDate) : null,
      endDate: level.endDate ? toFormat(level.endDate) : null,
      startDateIso: level.startDate,
      endDateIso: level.endDate ?? null,
      completeCode: level.completeCode ?? null,
      remarks: level.remarks ?? null,
      detailsCount: details.length,
      completedDetailsCount: details.filter((d) => d.completed).length,
      status: isArchived ? "Archived" : level.completed ? "Complete" : "In Progress",
      completed: isArchived ? false : level.completed,
      expiresDate: isArchived ? null : level.levelExpirationDate ? toFormat(level.levelExpirationDate) : null,
      isExpired: isArchived ? false : level.levelExpirationDate ? !isInFuture(level.levelExpirationDate) : false,
      details
    };
  });
};
